<template>
  <b-card no-body class="h-100 bg-dark-navy custom border-0" v-if="totals">
    <template v-slot:header></template>
    <b-card-body class="h-100 p-0">
      <b-tabs card pills class="h-100" nav-wrapper-class="nav-wrapper rounded-0 border-0 pt-0" content-class="overflow-y scroll-overflow h-100" fill v-model="tabIndex">
        <b-tab title="COVID Analysis"  active>
          <h4 class="text-white pb-1 mb-5 ml-3 bottom-border-yellow" style="width: 25%">COVID Analysis<span class="color-yellow pl-1">:</span></h4>
          <b-row>
            <b-col md="4" offset-md="1">
              <b-table responsive hover :items="covidTable" :fields="covidTableFields('Display')" class="mb-5">
                <template #cell(value)="data">
                  {{ data.value | number }}
                </template>
              </b-table>
              <apexchart type="donut" :options="covidTableChart.options" :series="covidTableChart.data" v-if="tabIndex === 0"></apexchart>
            </b-col>
            <b-col md="6">
              <p class="text-white">There were <strong class="text-white">{{ covidTotals | number }}</strong> chest x-rays submitted to the COVID platform to triage and rule out COVID pneumonia. The images were triaged according to the RADIFY&trade; findings into:</p>
              <ul class="pl-3">
                <li class="text-white mb-3"><strong class="text-white">High Probability</strong>, i.e. typical appearance: peripheral, bilateral or multifocal ground glass opacity and /or consolidation and /or infiltrates.<br /><br /><strong class="text-white">{{ totals.covid.classic | number }}</strong> images displayed features of <strong class="text-white">High Probability</strong>.</li>
                <li class="text-white mb-3"><strong class="text-white">Intermediate Probability</strong>, i.e. intermediate appearance: not specific or absence of typical appearance, but ground glass, consolidation, infiltrates noted.<br /><br /><strong class="text-white">{{ totals.covid.probable | number }}</strong> images displayed features of <strong class="text-white">Intermediate Probability</strong>.</li>
                <li class="text-white mb-3"><strong class="text-white">Low Probability</strong>, i.e. does not fit into classic or intermediate appearance. Patterns on chest x-ray suggest alternative diagnosis, eg. mass, lesion /malignancy, interstitial lung disease.<br /><br /><strong class="text-white">{{ totals.covid.intermediate | number }}</strong> images displayed features of <strong class="text-white">Low Probability</strong>.</li>
                <li class="text-white mb-3"><strong class="text-white">Non-COVID</strong>, i.e. atypical appearance: lung cavitation, nodules, pleural effusions, lymphadenopathy, mass, pulmonary fibrosis.<br /><br /><strong class="text-white">{{ totals.covid.noncovid | number }}</strong> images displayed features <strong class="text-white">not suggestive</strong> of typical features COVID pneumonia.</li>
                <li class="text-white mb-3"><strong class="text-white">No Pathologies</strong> detected on <strong class="text-white">{{ totals.covid.negative + totals.covid.none | number }}</strong> images.</li>
              </ul>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab title="AI Confidence">
          <h4 class="text-white pb-1 mb-5 ml-3 bottom-border-yellow" style="width: 25%">AI Confidence<span class="color-yellow pl-1">:</span></h4>
          <b-row>
            <b-col md="4" offset-md="1">
              <b-table responsive hover :items="aiTable" :fields="aiTableFields('Display')" class="mb-5">
                <template #cell(value)="data">
                  {{ data.value | decimal }}%
                </template>
              </b-table>
              <p class="text-white"><strong class="text-white">Note</strong>: Only detected pathologies with an Confidence Above 50% are reported.</p>
              <p class="text-white">RADIFY achieved an Confidence above 50% in <strong class="text-white">{{ totals.confidence.aveOver50 | decimal }}%</strong> of images on average with the maximum AI Confidence of <strong class="text-white">{{ totals.confidence.max | decimal }}%</strong></p>
            </b-col>
            <b-col md="7">
              <b-row>
                <b-col md="6">
                  <apexchart type="donut" :options="ai1TableChart.options" :series="ai1TableChart.data" v-if="tabIndex === 1"></apexchart>
                </b-col>
                <b-col offset-md="4" md="6" style="margin-top: -6rem; margin-bottom: -6rem;">
                  <apexchart type="donut" :options="ai2TableChart.options" :series="ai2TableChart.data" v-if="tabIndex === 1"></apexchart>
                </b-col>
                <b-col md="6">
                  <apexchart type="donut" :options="ai3TableChart.options" :series="ai3TableChart.data" v-if="tabIndex === 1"></apexchart>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab title="Patients &amp; Studies">
          <h4 class="text-white pb-1 mb-5 ml-3 bottom-border-yellow" style="width: 25%">Total Patients<span class="color-yellow px-1">:</span> {{ totals.studies.patients | number }}</h4>
          <b-row>
            <b-col md="4" offset-md="1">
              <b-table responsive hover :items="patient1Table" :fields="patient1TableFields('Display')" class="mb-5">
                <template #cell(value)="data">
                  <span v-if="data.item.name === 'Average per Patient'">{{ data.value | decimal }}</span>
                  <span v-else>{{ data.value | number }}</span>
                </template>
              </b-table>
              <b-table responsive hover :items="patient2Table" :fields="patient2TableFields('Display')" class="mb-5">
                <template #cell(value)="data">
                  <span v-if="data.item.name === 'Average per Study'">{{ data.value | decimal }}</span>
                  <span v-else>{{ data.value | number }}</span>
                </template>
              </b-table>
            </b-col>
            <b-col md="6">
              <b-table responsive hover :items="patient3Table" :fields="patient3TableFields('Display')" class="mb-5">
                <template #cell(value)="data">
                  <span v-if="data.item.name === 'Average per Image'">{{ data.value | decimal }}</span>
                  <span v-else>{{ data.value | number }}</span>
                </template>
              </b-table>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab title="Review" class="p-3">
          <h4 class="text-white pb-1 mb-5 ml-3 bottom-border-yellow" style="width: 25%">Review<span class="color-yellow pl-1">:</span></h4>
          <b-row>
            <b-col md="4" offset-md="1">
              <b-table responsive hover :items="reviewTable" :fields="reviewTableFields('Display')" class="mb-5">
                <template #cell(value)="data">
                  {{ data.value | number }}
                </template>
              </b-table>
              <apexchart type="donut" :options="reviewTableChart.options" :series="reviewTableChart.data" v-if="tabIndex === 3"></apexchart>
            </b-col>
            <b-col md="6">
              <p class="text-white">Independent radiologists validated and reviewed <strong class="text-white">{{ totals.pathologies.validated | number }}</strong> images processed by RADIFY&trade;.</p>
              <p class="text-white">There was a concordance with the AI findings on <strong class="text-white">{{ totals.pathologies.concordance | number }}</strong> of the <strong class="text-white">{{ totals.pathologies.validated | number }}</strong> images, reflecting <strong class="text-white">{{ (totals.pathologies.concordance / totals.pathologies.validated * 100).toFixed(2) }}%</strong> concordance.</p>
              <p class="text-white">There were <strong class="text-white">{{ totals.pathologies.validated - totals.pathologies.concordance | number }}</strong> images where radiologists did not agree or amended the findings on review, reflecting <strong class="text-white">{{ ((totals.pathologies.validated - totals.pathologies.concordance) / totals.pathologies.validated * 100).toFixed(2) }}%</strong> discordance.</p>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab title="Pathologies">
          <h4 class="text-white pb-1 mb-5 ml-3 bottom-border-yellow" style="width: 25%">Pathologies<span class="color-yellow pl-1">:</span></h4>
          <b-row>
            <b-col md="10" offset-md="1">
              <apexchart type="bar" :options="pathologiesTableChart.options" :series="pathologiesTableChart.data" v-if="tabIndex === 4"></apexchart>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6" offset-md="1">
              <b-table responsive hover :items="pathologiesTable" :fields="pathologiesTableFields('Display')" class="mb-5">
                <template #cell(value)="data">
                  {{ data.value | number }}
                </template>
              </b-table>
            </b-col>
            <b-col md="4">
              <p class="text-white">A total of <strong class="text-white">{{ totals.pathologies.images | number }}</strong> images were identified and labelled with a total of <strong class="text-white">{{ totals.pathologies.count | number }}</strong> pathologies.</p>
              <p class="text-white"><strong class="text-white">{{ pathologiesTable[0].name }}</strong>, <strong class="text-white">{{ pathologiesTable[1].name }}</strong> and <strong class="text-white">{{ pathologiesTable[2].name }}</strong> were the top 3 pathologies identified.</p>
            </b-col>
          </b-row>
        </b-tab>
      </b-tabs>
    </b-card-body>
  </b-card>
</template>

<script>

import numeral from 'numeral'

export default {
  data () {
    return {
      tabIndex: 0,
      list: [
        'Atelectasis',
        'Bone Lesion ',
        'Bronchiectasis',
        'Bullae',
        'Cardiomegaly ',
        'Cyst',
        'Calcified Nodules',
        'CP Angle Obliteration',
        'Consolidation',
        'Cavity ',
        'Calcified Diaphragm',
        'Elevated Diaphragm',
        'Calcification Egg Shell',
        'Emphysema',
        'Fibrosis',
        'Foreign Body',
        'Ground Glass',
        'Hyperinflation',
        'Reticular Infiltrates',
        'Infiltrates',
        'Interstitial Infiltrates',
        'Irregular Opacities',
        'Lymphadenopathy',
        'Micronodules',
        'Mass',
        'Nodules',
        'Fibrosis',
        'Other',
        'Pleural Effusion ',
        'Pleural Plaques',
        'Pleural Thickening',
        'Pneumothorax',
        'Pneumomediastinum',
        'Peribronchial Thickening',
        'Perihilar Infiltrates',
        'Post Surgical Hardware',
        'Pulmonary Arteries Enlarged',
        'Reticular Infiltrate',
        'Scoliosis',
        'Soft Tissue Emphysema',
        'Surgical Clips',
        'Unfolded Aorta'
      ]
    }
  },
  props: {
    totals: Object
  },
  filters: {
    number: function (value) {
      if (!value) return ''
      return numeral(value).format('0,0')
    },
    decimal: function (value) {
      if (!value) return ''
      return numeral(value).format('0,0.00')
    }
  },
  computed: {
    reviewTable () {
      if (this.totals && this.totals.pathologies) {
        return [
          { name: 'Concordance', value: this.totals.pathologies.concordance },
          { name: 'Discordance', value: this.totals.pathologies.validated - this.totals.pathologies.concordance }
        ]
      } else {
        return []
      }
    },
    reviewTableChart () {
      if (this.totals && this.totals.pathologies) {
        return {
          data: [
            this.totals.pathologies.concordance,
            (this.totals.pathologies.validated - this.totals.pathologies.concordance)
          ],
          options: {
            colors: ['#03cef6', '#f0B93b'],
            stroke: {
              colors: ['#081624'],
              width: 3
            },
            legend: {
              show: false
            },
            labels: ['Concordance', 'Discordance'],
            dataLabels: {
              formatter: function (value) {
                return value.toFixed(2) + '%'
              },
              style: {
                fontFamily: 'Lato, sans-serif'
              }
            },
            plotOptions: {
              pie: {
                expandOnClick: true
              }
            },
            chart: {
              events: {
                animationEnd: function (ctx) {
                  ctx.toggleDataPointSelection(0)
                }
              }
            }
          }
        }
      } else {
        return null
      }
    },
    reviewTableChartPDF () {
      if (this.totals && this.totals.pathologies) {
        return {
          data: [
            this.totals.pathologies.concordance,
            (this.totals.pathologies.validated - this.totals.pathologies.concordance)
          ],
          options: {
            colors: ['#03cef6', '#f0B93b'],
            stroke: {
              colors: ['#FFF'],
              width: 3
            },
            legend: {
              show: true,
              position: 'bottom'
            },
            labels: ['Concordance', 'Discordance'],
            dataLabels: {
              formatter: function (value) {
                return value.toFixed(2) + '%'
              },
              style: {
                fontFamily: 'Lato, sans-serif',
                colors: ['#000']
              }
            }
          }
        }
      } else {
        return null
      }
    },
    covidTable () {
      if (this.totals && this.totals.covid) {
        return [
          { name: 'High Probability', value: this.totals.covid.classic },
          { name: 'Intermediate Probability', value: this.totals.covid.probable },
          { name: 'Low Probability', value: this.totals.covid.intermediate },
          { name: 'Non-COVID', value: this.totals.covid.noncovid },
          { name: 'No Pathologies', value: this.totals.covid.negative + this.totals.covid.none }
        ]
      } else {
        return []
      }
    },
    covidTableChart () {
      if (this.totals && this.totals.covid) {
        return {
          data: this.covidTable.filter(_record => _record.name !== 'No Pathologies').map(_record => _record.value),
          options: {
            colors: ['#ff5348', '#fd7e14', '#f0B93b', '#03cef6', '#2673bc'],
            stroke: {
              colors: ['#081624'],
              width: 3
            },
            legend: {
              show: false
            },
            labels: this.covidTable.filter(_record => _record.name !== 'No Pathologies').map(_record => _record.name),
            dataLabels: {
              formatter: function (value) {
                return value.toFixed(2) + '%'
              },
              style: {
                fontFamily: 'Lato, sans-serif'
              }
            },
            plotOptions: {
              pie: {
                expandOnClick: true
              }
            },
            chart: {
              events: {
                animationEnd: function (ctx) {
                  ctx.toggleDataPointSelection(0)
                }
              }
            }
          }
        }
      } else {
        return null
      }
    },
    covidTableChartPDF () {
      if (this.totals && this.totals.covid) {
        return {
          data: this.covidTable.filter(_record => _record.name !== 'No Pathologies').map(_record => _record.value),
          options: {
            colors: ['#ff5348', '#fd7e14', '#f0B93b', '#03cef6', '#2673bc'],
            stroke: {
              colors: ['#FFF'],
              width: 3
            },
            legend: {
              show: true,
              position: 'bottom'
            },
            labels: this.covidTable.filter(_record => _record.name !== 'No Pathologies').map(_record => _record.name),
            dataLabels: {
              formatter: function (value) {
                return value.toFixed(2) + '%'
              },
              style: {
                fontFamily: 'Lato, sans-serif',
                colors: ['#000']
              }
            },
            plotOptions: {
              pie: {
                expandOnClick: true
              }
            },
            chart: {
              events: {
                animationEnd: function (ctx) {
                  // ctx.toggleDataPointSelection(0)
                }
              }
            }
          }
        }
      } else {
        return null
      }
    },
    covidTotals () {
      if (this.totals && this.totals.covid) {
        let _total = 0
        for (const _prop in this.totals.covid) {
          _total += this.totals.covid[_prop]
        }

        return _total
      } else {
        return 0
      }
    },
    aiTable () {
      if (this.totals && this.totals.confidence) {
        return [
          { name: 'Confidence Minimum', value: this.totals.confidence.min },
          { name: 'Confidence Maximum', value: this.totals.confidence.max },
          { name: 'Confidence Average', value: this.totals.confidence.ave }
        ]
      } else {
        return []
      }
    },
    ai1TableChart () {
      if (this.totals && this.totals.confidence) {
        return {
          data: [
            Math.round(this.totals.confidence.min * 100) / 100,
            Math.round((100 - this.totals.confidence.min) * 100) / 100
          ],
          options: {
            colors: ['#03cef6', '#f0B93b'],
            stroke: {
              colors: ['#081624'],
              width: 3
            },
            legend: {
              show: false
            },
            labels: ['Confidence Minumum', ''],
            dataLabels: {
              formatter: function (value) {
                return value.toFixed(2) + '%'
              },
              style: {
                fontFamily: 'Lato, sans-serif'
              }
            },
            plotOptions: {
              pie: {
                expandOnClick: true
              }
            },
            chart: {
              events: {
                animationEnd: function (ctx) {
                  ctx.toggleDataPointSelection(0)
                }
              }
            }
          }
        }
      } else {
        return null
      }
    },
    ai2TableChart () {
      if (this.totals && this.totals.confidence) {
        return {
          data: [
            Math.round(this.totals.confidence.max * 100) / 100,
            Math.round((100 - this.totals.confidence.max) * 100) / 100
          ],
          options: {
            colors: ['#03cef6', '#f0B93b'],
            stroke: {
              colors: ['#081624'],
              width: 3
            },
            legend: {
              show: false
            },
            labels: ['Confidence Maximum', ''],
            dataLabels: {
              formatter: function (value) {
                return value.toFixed(2) + '%'
              },
              style: {
                fontFamily: 'Lato, sans-serif'
              }
            },
            plotOptions: {
              pie: {
                expandOnClick: true
              }
            },
            chart: {
              events: {
                animationEnd: function (ctx) {
                  ctx.toggleDataPointSelection(0)
                }
              }
            }
          }
        }
      } else {
        return null
      }
    },
    ai3TableChart () {
      if (this.totals && this.totals.confidence) {
        return {
          data: [
            Math.round(this.totals.confidence.aveOver50 * 100) / 100,
            Math.round((100 - this.totals.confidence.aveOver50) * 100) / 100
          ],
          options: {
            colors: ['#03cef6', '#f0B93b'],
            stroke: {
              colors: ['#081624'],
              width: 3
            },
            legend: {
              show: false
            },
            labels: ['Confidence Above 50% Average', ''],
            dataLabels: {
              formatter: function (value) {
                return value.toFixed(2) + '%'
              },
              style: {
                fontFamily: 'Lato, sans-serif'
              }
            },
            plotOptions: {
              pie: {
                expandOnClick: true
              }
            },
            chart: {
              events: {
                animationEnd: function (ctx) {
                  ctx.toggleDataPointSelection(0)
                }
              }
            }
          }
        }
      } else {
        return null
      }
    },
    ai1TableChartPDF () {
      if (this.totals && this.totals.confidence) {
        return {
          data: [
            Math.round(this.totals.confidence.min * 100) / 100,
            Math.round((100 - this.totals.confidence.min) * 100) / 100
          ],
          options: {
            colors: ['#03cef6', '#f0B93b'],
            stroke: {
              colors: ['#FFF'],
              width: 3
            },
            legend: {
              show: true,
              position: 'bottom'
            },
            labels: ['Confidence Minumum', ''],
            dataLabels: {
              formatter: function (value) {
                return value.toFixed(2) + '%'
              },
              style: {
                fontFamily: 'Lato, sans-serif',
                colors: ['#000']
              }
            }
          }
        }
      } else {
        return null
      }
    },
    ai2TableChartPDF () {
      if (this.totals && this.totals.confidence) {
        return {
          data: [
            Math.round(this.totals.confidence.max * 100) / 100,
            Math.round((100 - this.totals.confidence.max) * 100) / 100
          ],
          options: {
            colors: ['#03cef6', '#f0B93b'],
            stroke: {
              colors: ['#FFF'],
              width: 3
            },
            legend: {
              show: true,
              position: 'bottom'
            },
            labels: ['Confidence Maximum', ''],
            dataLabels: {
              formatter: function (value) {
                return value.toFixed(2) + '%'
              },
              style: {
                fontFamily: 'Lato, sans-serif',
                colors: ['#000']
              }
            }
          }
        }
      } else {
        return null
      }
    },
    ai3TableChartPDF () {
      if (this.totals && this.totals.confidence) {
        return {
          data: [
            Math.round(this.totals.confidence.aveOver50 * 100) / 100,
            Math.round((100 - this.totals.confidence.aveOver50) * 100) / 100
          ],
          options: {
            colors: ['#03cef6', '#f0B93b'],
            stroke: {
              colors: ['#FFF'],
              width: 3
            },
            legend: {
              show: true,
              position: 'bottom'
            },
            labels: ['Confidence Above 50% Average', ''],
            dataLabels: {
              formatter: function (value) {
                return value.toFixed(2) + '%'
              },
              style: {
                fontFamily: 'Lato, sans-serif',
                colors: ['#000']
              }
            }
          }
        }
      } else {
        return null
      }
    },
    patient1Table () {
      if (this.totals && this.totals.studies) {
        return [
          { name: 'Minimum per Patient', value: this.totals.studies.minPerPatient },
          { name: 'Maximum per Patient', value: this.totals.studies.maxPerPatient },
          { name: 'Average per Patient', value: this.totals.studies.studiesTotal / this.totals.studies.patients }
        ]
      } else {
        return []
      }
    },
    patient2Table () {
      if (this.totals && this.totals.responses) {
        return [
          { name: 'Minimum per Study', value: this.totals.responses.min },
          { name: 'Maximum per Study', value: this.totals.responses.max },
          { name: 'Average per Study', value: this.totals.responses.count / this.totals.studies.studiesTotal }
        ]
      } else {
        return []
      }
    },
    patient3Table () {
      if (this.totals && this.totals.pathologies) {
        return [
          { name: 'Images with Pathologies', value: this.totals.pathologies.images },
          { name: 'Minimum per Image', value: this.totals.pathologies.min },
          { name: 'Maximum per Image', value: this.totals.pathologies.max },
          { name: 'Average per Image', value: this.totals.pathologies.count / this.totals.pathologies.images }
        ]
      } else {
        return []
      }
    },
    pathologiesTable () {
      if (this.totals && this.totals.pathologies && this.totals.pathologies.pathologies) {
        const _pathologies = [{
          value: 0,
          name: 'Other'
        }]
        this.totals.pathologies.pathologies.forEach(_pathology => {
          const __pathology = this.list.find(_name => _name.split(' ').join('').toLowerCase() === _pathology.name)

          if (_pathology.count > 1 && _pathology.name !== 'Other' && __pathology) {
            _pathologies.push({
              value: _pathology.count,
              name: this.capitalise(__pathology)
            })
          } else {
            const _other = _pathologies.find(_pathology => _pathology.name === 'Other')
            _other.value += _pathology.count
          }
        })
        _pathologies.sort((a, b) => (a.value < b.value) ? 1 : -1)

        return _pathologies
      } else {
        return []
      }
    },
    pathologiesTableChart () {
      if (this.totals && this.totals.pathologies && this.totals.pathologies.pathologies) {
        return {
          data: [{
            name: 'Count',
            data: this.pathologiesTable.map(_record => _record.value)
          }],
          options: {
            colors: ['#2673bc'],
            stroke: {
              width: 3
            },
            legend: {
              show: false
            },
            xaxis: {
              type: 'category',
              labels: {
                show: true,
                style: {
                  colors: '#FFFFFF'
                }
              },
              categories: this.pathologiesTable.map(_record => _record.name),
              title: {
                text: 'Pathologies',
                style: {
                  color: '#FFF'
                }
              }
            },
            yaxis: {
              labels: {
                style: {
                  colors: '#FFFFFF'
                }
              },
              title: {
                text: 'Count',
                style: {
                  color: '#FFF'
                }
              }
            },
            dataLabels: {
              enabled: false
            },
            chart: {
              toolbar: {
                show: false
              }
            }
          }
        }
      } else {
        return null
      }
    },
    pathologiesTableChartPDF () {
      if (this.totals && this.totals.pathologies && this.totals.pathologies.pathologies) {
        return {
          data: [{
            name: 'Count',
            data: this.pathologiesTable.map(_record => _record.value)
          }],
          options: {
            colors: ['#2673bc'],
            stroke: {
              width: 3
            },
            legend: {
              show: false
            },
            xaxis: {
              type: 'category',
              labels: {
                show: true,
                style: {
                }
              },
              categories: this.pathologiesTable.map(_record => _record.name)
            },
            yaxis: {
              labels: {
                style: {
                }
              }
            },
            dataLabels: {
              enabled: false
            },
            chart: {
              toolbar: {
                show: false
              }
            }
          }
        }
      } else {
        return null
      }
    }
  },
  methods: {
    capitalise: function (value) {
      if (!value) return value

      const _separated = value.toLowerCase().split(' ')
      for (let i = 0; i < _separated.length; i++) {
        _separated[i] = _separated[i].charAt(0).toUpperCase() + _separated[i].substring(1)
      }

      return _separated.join(' ')
    },
    covidTableFields: function (placement) {
      return [
        { key: 'name', label: 'COVID Analysis', class: 'py-1' + (placement === 'Display' ? ' color-white' : '') },
        { key: 'value', label: numeral(this.covidTotals).format('0,0'), class: 'text-right py-1' + (placement === 'Display' ? ' color-white' : '') }
      ]
    },
    aiTableFields: function (placement) {
      return [
        { key: 'name', label: 'Confidence Above 50% Average', class: 'py-1' + (placement === 'Display' ? ' color-white' : '') },
        { key: 'value', label: numeral(this.totals.confidence.aveOver50).format('0,0.00') + '%', class: 'text-right py-1' + (placement === 'Display' ? ' color-white' : '') }
      ]
    },
    patient1TableFields: function (placement) {
      return [
        { key: 'name', label: 'Total Studies', class: 'py-1' + (placement === 'Display' ? ' color-white' : '') },
        { key: 'value', label: numeral(this.totals.studies.studiesTotal).format('0,0'), class: 'text-right py-1' + (placement === 'Display' ? ' color-white' : '') }
      ]
    },
    patient2TableFields: function (placement) {
      return [
        { key: 'name', label: 'Total Images', class: 'py-1' + (placement === 'Display' ? ' color-white' : '') },
        { key: 'value', label: numeral(this.totals.responses.count).format('0,0'), class: 'text-right py-1' + (placement === 'Display' ? ' color-white' : '') }
      ]
    },
    patient3TableFields: function (placement) {
      return [
        { key: 'name', label: 'Total Pathologies', class: 'py-1' + (placement === 'Display' ? ' color-white' : '') },
        { key: 'value', label: numeral(this.totals.pathologies.count).format('0,0'), class: 'text-right py-1' + (placement === 'Display' ? ' color-white' : '') }
      ]
    },
    reviewTableFields: function (placement) {
      return [
        { key: 'name', label: 'Studies reviewed by Radiologist', class: 'py-1' + (placement === 'Display' ? ' color-white' : '') },
        { key: 'value', label: numeral(this.totals.pathologies.validated).format('0,0'), class: 'text-right py-1' + (placement === 'Display' ? ' color-white' : '') }
      ]
    },
    pathologiesTableFields: function (placement) {
      return [
        { key: 'name', label: 'Total Pathologies', class: 'py-1' + (placement === 'Display' ? ' color-white' : '') },
        { key: 'value', label: numeral(this.totals.pathologies.count).format('0,0'), class: 'text-right py-1' + (placement === 'Display' ? ' color-white' : '') }
      ]
    },
    pathologiesTablePDF: function (segment) {
      console.log('path table check')
      if (this.totals && this.totals.pathologies && this.totals.pathologies.pathologies) {
        const _pathologies = [{
          value: 0,
          name: 'Other'
        }]
        this.totals.pathologies.pathologies.forEach(_pathology => {
          const __pathology = this.list.find(_name => _name.split(' ').join('').toLowerCase() === _pathology.name)

          if (_pathology.count > 1 && _pathology.name !== 'Other' && __pathology) {
            _pathologies.push({
              value: _pathology.count,
              name: this.capitalise(__pathology)
            })
          } else {
            const _other = _pathologies.find(_pathology => _pathology.name === 'Other')
            _other.value += _pathology.count
          }
        })
        _pathologies.sort((a, b) => (a.value < b.value) ? 1 : -1)
        const half = Math.ceil(_pathologies.length / 2)
        const firstHalf = _pathologies.slice(0, half)
        const secondHalf = _pathologies.slice(-half)
        if (segment === 0) {
          return firstHalf
        } else {
          return secondHalf
        }
        // const returnList = [firstHalf, secondHalf]
        // console.log(returnList)
        // return returnList
      } else {
        return []
      }
    }
  },
  mounted () {
    // this.$bvModal.show('modal-wrapper')
    // this.$bvModal.hide('modal-wrapper')
  }
}
</script>
